import React, { useState } from 'react';
import { format, addDays, startOfWeek, isSameDay, isToday, isBefore, startOfDay } from 'date-fns';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const BookingCalendar = ({ selectedDate, onDateSelect }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const days = Array.from({ length: 7 }, (_, i) => addDays(currentWeekStart, i));

  const goToPreviousWeek = () => {
    const newWeekStart = addDays(currentWeekStart, -7);
    // Don't allow going to past weeks
    if (!isBefore(newWeekStart, startOfWeek(new Date(), { weekStartsOn: 1 }))) {
      setCurrentWeekStart(newWeekStart);
    }
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(addDays(currentWeekStart, 7));
  };

  const canGoPrevious = !isBefore(
    addDays(currentWeekStart, -7),
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Select Date</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={goToPreviousWeek}
            disabled={!canGoPrevious}
            className={`p-2 rounded-lg transition-colors ${
              canGoPrevious
                ? 'hover:bg-gray-100 text-gray-700'
                : 'text-gray-300 cursor-not-allowed'
            }`}
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <span className="text-sm font-medium text-gray-600 min-w-[120px] text-center">
            {format(currentWeekStart, 'MMM d')} - {format(addDays(currentWeekStart, 6), 'MMM d, yyyy')}
          </span>
          <button
            onClick={goToNextWeek}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
          >
            <ChevronRightIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2">
        {days.map((day) => {
          const isSelected = selectedDate && isSameDay(day, selectedDate);
          const isPast = isBefore(day, startOfDay(new Date()));
          const todayDate = isToday(day);

          return (
            <button
              key={day.toISOString()}
              onClick={() => !isPast && onDateSelect(day)}
              disabled={isPast}
              className={`
                p-4 rounded-lg text-center transition-all
                ${isPast ? 'opacity-40 cursor-not-allowed bg-gray-50' : 'hover:bg-primary-50'}
                ${isSelected ? 'bg-primary-500 text-white hover:bg-primary-600' : ''}
                ${todayDate && !isSelected ? 'border-2 border-primary-500' : ''}
              `}
            >
              <div className="text-xs font-medium mb-1 uppercase">
                {format(day, 'EEE')}
              </div>
              <div className={`text-lg font-semibold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                {format(day, 'd')}
              </div>
            </button>
          );
        })}
      </div>

      {selectedDate && (
        <div className="mt-4 p-3 bg-primary-50 rounded-lg text-center">
          <p className="text-sm text-primary-800 font-medium">
            Selected: {format(selectedDate, 'EEEE, MMMM d, yyyy')}
          </p>
        </div>
      )}
    </div>
  );
};

export default BookingCalendar;
